import {
  createStore,
  applyMiddleware,
  combineReducers,
  compose,
  Reducer,
  AnyAction,
  Store,
  StoreEnhancer
} from 'redux'
import {
  routerMiddleware,
  routerReducer
} from 'react-router-redux'
// eslint-disable-next-line import/no-extraneous-dependencies
import { createBrowserHistory } from 'history'
import thunk from 'redux-thunk'
import * as Sentry from '@sentry/browser'
// Local
import cdiQueryReducer from '../reducer/cdiQuery'
import conditionReducer from '../reducer/condition'
import encounterReducer from '../reducer/encounter'
import icdReducer from '../reducer/icd'
import patientReducer from '../reducer/patient'
import coderReducer from '../reducer/coder'
import historyReducer from '../reducer/history'
import versionReducer from '../reducer/version'
import practitionerReducer from '../reducer/practitioner'
import userReducer, { CLEAR_ALL_STATE } from '../reducer/user'
import crashReporter from '../middleware/crashReporter'
import logger from '../middleware/logger'
import { Middleware } from 'redux'

declare global {
  interface Window {
    __REDUX_DEVTOOLS_EXTENSION__?: () => StoreEnhancer
  }
}

type PersistedUser = {
  userId?: string | null
  isAuthenticated?: boolean
  tenantId?: string
  username?: string
  alias?: string
  type?: string
  roles?: string[]
  tenantProperties?: unknown[]
  userProperties?: unknown[]
  worklistFilters?: unknown[]
  appointmentlistFilters?: unknown[]
  patientListFilters?: unknown
}

type PersistedState = {
  user: PersistedUser
}

export const history = createBrowserHistory({
  basename: '/'
})

const storedState = localStorage.getItem('reduxState')
const persistedState: PersistedState = {
  user: storedState
    ? JSON.parse(storedState) as PersistedUser
    : {}
}

// If the persisted state has a user associated, tell Sentry.
if (
  persistedState.user &&
  persistedState.user.userId !== null &&
  persistedState.user.isAuthenticated
) {
  Sentry.configureScope(scope => {
    scope.setUser({ id: persistedState.user.userId || undefined })
  })
}

const enhancers: StoreEnhancer[] = []

const middleware: any = [
  thunk,
  routerMiddleware(history),
  logger,
  crashReporter
]

const appReducer = combineReducers({
  cdiQuery: cdiQueryReducer,
  condition: conditionReducer,
  encounter: encounterReducer,
  user: userReducer,
  coder: coderReducer,
  patient: patientReducer,
  router: routerReducer,
  icd: icdReducer,
  history: historyReducer,
  version: versionReducer,
  practitioner: practitionerReducer
})

export type RootState = ReturnType<typeof appReducer>

// @see https://alligator.io/redux/reset-state-redux/
const rootReducer: Reducer<RootState | undefined, AnyAction> = (
  state,
  action
) => {
  if (action.type === CLEAR_ALL_STATE) {
    return appReducer(undefined, action)
  }
  return appReducer(state, action)
}

export { appReducer }

if (process.env.NODE_ENV === 'development') {
  // eslint-disable-next-line no-underscore-dangle
  const devToolsExtension = window.__REDUX_DEVTOOLS_EXTENSION__

  if (typeof devToolsExtension === 'function') {
    enhancers.push(devToolsExtension())
  }
}

const composedEnhancers = compose(
  applyMiddleware(...middleware),
  ...enhancers
)

const store: Store = createStore(
  rootReducer as Reducer,
  persistedState,
  composedEnhancers as StoreEnhancer
)

store.subscribe(() => {
  const currentUser = store.getState().user
  localStorage.setItem(
    'reduxState',
    JSON.stringify({
      isAuthenticated: currentUser.isAuthenticated,
      tenantId: currentUser.tenantId,
      userId: currentUser.userId,
      username: currentUser.username,
      alias: currentUser.alias,
      type: currentUser.type,
      roles: currentUser.roles,
      tenantProperties: currentUser.tenantProperties,
      userProperties: currentUser.userProperties,
      worklistFilters: currentUser.worklistFilters,
      appointmentlistFilters: currentUser.appointmentlistFilters,
      patientListFilters: currentUser.patientListFilters
    })
  )
})

export default store
